import { useRef, useState, type RefObject } from "react";
import { styled } from "@pigment-css/react";
import {
  CalendarDays,
  ChevronGrabberVertical,
  ChevronGrabberVerticalThin,
  ChevronLeft,
  PencilLine,
} from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useCreateTodo } from "@acto/taskpane/core/data-access/todos/useCreateTodo";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { AssigneePickerSheet } from "@acto/taskpane/field-sales-2/components/patterns/AssigneePickerSheet";
import type { User } from "@acto/taskpane/core/domain";
import { useAssigneeToggle } from "@acto/taskpane/field-sales-2/data-access/todos/useAssigneeToggle";
import { BottomSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { useAutoFocusNoScroll } from "@acto/taskpane/field-sales-2/components/primitives/useAutoFocusNoScroll";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { DatePicker } from "@acto/taskpane/field-sales-2/components/primitives/DatePicker";
import { todayUtc, toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { ExpandableAccordion } from "./ui/ExpandableAccordion";

const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
}));

const Card = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  marginInline: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

// The due-date card delegates its spacing to its children: gap is dropped (a fixed gap would
// leave a phantom band above the collapsed row) and padding is dropped so the toggle button can
// fill the whole pill — making the entire glass surface the tap target, not just the text/value.
const DueDateCard = styled(Card)({
  gap: 0,
  padding: 0,
});

const DescriptionRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": {
    flexShrink: 0,
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
    marginTop: theme.fieldSalesTwo.spacing[2],
  },
}));

const DescriptionInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: "5.5rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

const DueDateRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  border: "none",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[38],
  '&[aria-expanded="true"]': {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  background: "transparent",
  transition: "background 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
  ...pressInteraction(theme, { scale: 1 }),
  // Disabled means "not clickable" — the chevron is dropped at the call site, but the label stays
  // full-contrast rather than greying out.
  "&:disabled": { pointerEvents: "none", cursor: "default" },
}));

const DueDateLeft = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

const DueDateValue = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
  "& svg": { width: theme.fieldSalesTwo.spacing[18], height: theme.fieldSalesTwo.spacing[18] },
}));

// 56px per Figma; fieldSalesTwo.spacing has no 56 key, so this min-height stays a rem literal.
const SubmitButton = styled(SolidCtaButton)({
  minHeight: "3.5rem",
});

// The due-date picker is weekday-only, so the default must be a weekday — today, or the next Monday
// when today is a weekend (otherwise the picker would show no selected day for the default).
const defaultDueDate = (): Date => {
  const today = todayUtc();
  const weekday = today.getUTCDay(); // 0 = Sun … 6 = Sat
  const offset = weekday === 6 ? 2 : weekday === 0 ? 1 : 0;
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + offset));
};

interface CreateTodoContentProps {
  clientId: string;
  onCreated: () => void;
  /** The sheet's scrollable Body — forwarded to the calendar accordion so it grows upward. */
  scrollRef: RefObject<HTMLDivElement | null>;
}

function CreateTodoContent({ clientId, onCreated, scrollRef }: CreateTodoContentProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const { locale } = useLocale();
  const { createTodo, result } = useCreateTodo();
  const track = useTrack();
  const { viewer } = useViewer();

  const descriptionRef = useAutoFocusNoScroll<HTMLTextAreaElement>();
  const [description, setDescription] = useState("");
  // Defaults to the next weekday; past dates stay selectable so a todo can be back-dated.
  const [dueDate, setDueDate] = useState<Date>(defaultDueDate);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  // null = no pick; the server then assigns the creating viewer.
  const [assignee, setAssignee] = useState<User | null>(null);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const assigneeRowRef = useRef<HTMLButtonElement>(null);

  const { disabled: assigneeDisabled } = useAssigneeToggle(clientId, assignee);

  const dueDateLabel = formatDayMonthYear(dueDate, locale);

  // description is required (title is optional and not collected here — see CreateTodoInput).
  const canSubmit = description.trim().length > 0 && !result.fetching;

  const handleCreate = async () => {
    if (!canSubmit) return;

    const created = await createTodo({
      id: crypto.randomUUID(),
      description: description.trim(),
      dueDate: toIsoDate(dueDate),
      clientId,
      assignedUserId: assignee?.id,
    });

    if (created) {
      track(AnalyticsEvents.TODO_CREATED, {
        todoId: created.id,
        customerId: clientId,
        source: "customer_details",
        hasDueDate: Boolean(dueDate),
      });
      onCreated();
    }
  };

  return (
    <>
      <Fields>
        <Card>
          <DescriptionRow>
            <PencilLine aria-hidden="true" />
            <DescriptionInput
              ref={descriptionRef}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("descriptionPlaceholder")}
              aria-label={t("descriptionPlaceholder")}
            />
          </DescriptionRow>
        </Card>

        <DueDateCard>
          <ExpandableAccordion calendarExpanded={calendarExpanded} scrollRef={scrollRef}>
            <DatePicker
              value={dueDate}
              includeWeekends={false}
              onChange={(date) => {
                setDueDate(date);
                setCalendarExpanded(false);
              }}
            />
            <Divider />
          </ExpandableAccordion>
          <DueDateRow
            type="button"
            aria-expanded={calendarExpanded}
            onClick={() => setCalendarExpanded((expanded) => !expanded)}
          >
            <DueDateLeft>
              <CalendarDays aria-hidden="true" />
              {t("dueDateLabel")}
            </DueDateLeft>
            <DueDateValue>
              {dueDateLabel}
              <ChevronGrabberVertical aria-hidden="true" />
            </DueDateValue>
          </DueDateRow>
        </DueDateCard>

        <DueDateCard>
          <DueDateRow
            ref={assigneeRowRef}
            type="button"
            onClick={() => setAssigneePickerOpen(true)}
            disabled={assigneeDisabled}
          >
            <DueDateLeft>{t("assigneeLabel")}</DueDateLeft>
            <DueDateValue>
              {assignee?.displayName ?? viewer?.displayName ?? t("assigneePlaceholder")}
              {!assigneeDisabled && <ChevronGrabberVerticalThin aria-hidden="true" />}
            </DueDateValue>
          </DueDateRow>
        </DueDateCard>
      </Fields>

      <CtaRow>
        <SubmitButton type="button" onClick={handleCreate} disabled={!canSubmit}>
          {t("submit")}
        </SubmitButton>
      </CtaRow>

      <AssigneePickerSheet
        isOpen={assigneePickerOpen}
        onOpenChange={setAssigneePickerOpen}
        anchorRef={assigneeRowRef}
        clientId={clientId}
        selectedId={assignee?.id}
        onSelect={setAssignee}
      />
    </>
  );
}

interface CreateTodoSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Fired after a todo is successfully created (sheet already closing) — e.g. to show a toast. */
  onCreated: () => void;
}

export function CreateTodoSheet({ isOpen, onOpenChange, clientId, onCreated }: CreateTodoSheetProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const bodyRef = useRef<HTMLDivElement>(null);

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("title")}
      closeAriaLabel={t("closeAriaLabel")}
      closeIcon={<ChevronLeft />}
      bodyRef={bodyRef}
    >
      <CreateTodoContent
        clientId={clientId}
        scrollRef={bodyRef}
        onCreated={() => {
          onOpenChange(false);
          onCreated();
        }}
      />
    </BottomSheet>
  );
}
